import { isSameDay } from 'date-fns';
import { DayShift } from '@/types';

/**
 * Unified utility for filtering shifts by date
 * Handles both string and Date formats for shift time properties
 */
export function getShiftsForDate(shifts: DayShift[], targetDate: Date): DayShift[] {
  if (!shifts || !Array.isArray(shifts)) {
    return [];
  }

  if (!targetDate || !(targetDate instanceof Date) || isNaN(targetDate.getTime())) {
    return [];
  }

  return shifts.filter(shift => {
    if (!shift) {
      return false;
    }

    // Handle both string and Date formats for startTime
    let shiftStartTime: Date;

    if (shift.startTime instanceof Date) {
      shiftStartTime = shift.startTime;
    } else if (typeof shift.startTime === 'string') {
      shiftStartTime = new Date(shift.startTime);
    } else if ((shift as any).start_time) {
      // Fallback to start_time property (database format)
      shiftStartTime = new Date((shift as any).start_time);
    } else {
      return false;
    }

    // Validate the parsed date
    if (isNaN(shiftStartTime.getTime())) {
      return false;
    }

    // Filter out historical shifts (they shouldn't be used for templates)
    if (shift.isHistorical) {
      return false;
    }

    // Filter out preview shifts (temporary shifts from template drops)
    if (shift.isPreview || shift.isPlanningPreview) {
      return false;
    }

    return isSameDay(shiftStartTime, targetDate);
  });
}

/**
 * Get non-historical shifts for a specific date (used by template saving)
 * This is the exact logic that should be used consistently across all components
 */
export function getNonHistoricalShiftsForDate(shifts: DayShift[], targetDate: Date): DayShift[] {
  const filteredShifts = getShiftsForDate(shifts, targetDate);
  return filteredShifts.filter(shift => !shift.isHistorical);
}